import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Card, Spinner } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PackagePlus } from "lucide-react";

function perPartStatus(p) {
  if ((p.ordered_qty || 0) === 0) return "NOT_ORDERED";
  if (p.accepted_qty >= p.required_qty) return "COMPLETE";
  if (p.overdue) return "OVERDUE";
  if ((p.accepted_qty || 0) > 0) return "PARTIAL";
  return "ORDERED";
}

export default function ProcurementDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)).finally(() => setLoading(false));
  }, [jobId]);

  if (loading) return <Spinner />;
  if (!job) return <div className="text-gray-400 p-6">Job tidak ditemukan.</div>;
  const parts = job.required_parts || [];

  return (
    <div>
      <button onClick={() => navigate("/part-pesan")} className="text-sm text-[var(--neon-cyan)] flex items-center gap-1 mb-3" data-testid="proc-back">
        <ArrowLeft size={15} /> Kembali ke Part Pesan
      </button>
      <PageHeader title="Procurement Detail" subtitle="Status per part: supplier, ETD, ordered, received, remaining.">
        <Button onClick={() => navigate(`/part-masuk?job=${job.id}`)} className="bg-gold text-white" data-testid="terima-part-btn">
          <PackagePlus size={16} className="mr-2" /> Terima Part
        </Button>
      </PageHeader>

      <Card className="p-5 mb-4" data-testid="proc-header">
        <div className="font-head font-bold text-xl text-white">
          <span className="font-mono-data">{job.claim_number || "NO CLAIM"}</span>
          <span className="text-gray-400"> — </span>
          <span className="font-mono-data">{job.nopol || "-"}</span>
        </div>
        <div className="text-sm text-gray-500 mt-1">
          {job.vehicle_brand || "-"} {job.vehicle_type || ""} • {job.insurance || "—"} • {job.job_number}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Part</th>
                <th className="text-left px-3 py-2">Part Number</th>
                <th className="text-center px-3 py-2">Req</th>
                <th className="text-center px-3 py-2">Ordered</th>
                <th className="text-left px-3 py-2">Supplier</th>
                <th className="text-left px-3 py-2">ETD</th>
                <th className="text-center px-3 py-2">Received</th>
                <th className="text-center px-3 py-2">Remaining</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => (
                <tr key={p.id} className="border-t border-gray-100" data-testid={`proc-part-${p.id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900">{p.part_name}</td>
                  <td className="px-3 py-2 font-mono-data text-xs text-gray-600">{p.part_number || "—"}</td>
                  <td className="px-3 py-2 text-center font-mono-data">{p.required_qty}</td>
                  <td className="px-3 py-2 text-center font-mono-data">{p.ordered_qty}</td>
                  <td className="px-3 py-2">{p.supplier || <span className="text-gray-400">—</span>}</td>
                  <td className="px-3 py-2 font-mono-data text-xs">{p.etd ? String(p.etd).slice(0, 10).split("-").reverse().join("-") : "—"}</td>
                  <td className="px-3 py-2 text-center font-mono-data">{p.accepted_qty}</td>
                  <td className="px-3 py-2 text-center font-mono-data">{p.remaining_qty}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={perPartStatus(p)} />
                    {p.accepted_qty > 0 && p.accepted_qty < p.required_qty && (
                      <span className="ml-2 text-[11px] text-[#FFD65C] font-medium" data-testid={`proc-part-${p.id}-partial`}>{p.accepted_qty}/{p.required_qty}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
